import { useState } from 'react'
import { ArrowUp, ArrowDown, ChevronsUpDown } from 'lucide-react'
import { TableHead } from '@/components/ui/table'
import { cn } from '@/lib/utils'

// Ordenação de tabela reutilizável (cabeçalho clicável + estado).
export type SortState<K extends string> = { key: K; dir: 'asc' | 'desc' }

export function useTableSort<K extends string>(key: K, dir: 'asc' | 'desc' = 'asc', descKeys: K[] = []) {
  const [sort, setSort] = useState<SortState<K>>({ key, dir })
  function toggle(k: K) {
    setSort((s) =>
      s.key === k
        ? { key: k, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { key: k, dir: descKeys.includes(k) ? 'desc' : 'asc' },
    )
  }
  return { sort, toggle }
}

// Compara dois valores (número ou texto) respeitando a direção.
export function cmp(a: string | number, b: string | number, dir: 'asc' | 'desc') {
  const c =
    typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b), 'pt-BR')
  return dir === 'asc' ? c : -c
}

export function SortHead<K extends string>({
  label,
  k,
  sort,
  onToggle,
  className,
}: {
  label: string
  k: K
  sort: SortState<K>
  onToggle: (k: K) => void
  className?: string
}) {
  const active = sort.key === k
  const Icon = active ? (sort.dir === 'asc' ? ArrowUp : ArrowDown) : ChevronsUpDown
  return (
    <TableHead
      className={cn('hover:text-foreground cursor-pointer select-none', className)}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {/* Botão real: clicável E operável por teclado (Enter/Espaço nativos). */}
      <button
        type="button"
        onClick={() => onToggle(k)}
        className="inline-flex cursor-pointer items-center gap-1 rounded bg-transparent text-inherit outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
      >
        {label}
        <Icon className={cn('size-3', active ? 'opacity-80' : 'opacity-30')} />
      </button>
    </TableHead>
  )
}
